import { useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { formatDate, formatNumber } from '@/components/live';
import { buildYieldStatementHtml, yieldTotals } from '@/lib/yieldStatement';

/** The Statement tab: the totals, and the sheet to open, print or download as a PDF. */
export function YieldStatementSheet({ harvest }: {
  harvest: { harvest_code: string | null; harvest_date: string; farmer_name: string | null; farmer_code: string | null; plot_code: string | null;
             species_label: string | null; trees_harvested: number; yield_basis: string | null; yield_formula: string;
             yield_lines: Array<Record<string, unknown>> };
}) {
  const { profile } = useAuth();
  const [busy, setBusy] = useState(false);
  const lines = (harvest.yield_lines ?? []).filter((line) => line.length_m != null || line.gross_t != null);
  if (!harvest.yield_basis || lines.length === 0) {
    return <p className="muted">No yield recorded yet. Choose how it was measured on Details and add the logs or loads.</p>;
  }
  const basis = harvest.yield_basis as 'log_volume' | 'weight';
  const html = () => buildYieldStatementHtml({
    organisation: profile?.organization?.name ?? '', harvestCode: harvest.harvest_code ?? '', harvestDate: formatDate(harvest.harvest_date),
    farmer: `${harvest.farmer_name ?? ''}${harvest.farmer_code ? ` (${harvest.farmer_code})` : ''}`, plot: harvest.plot_code ?? '',
    species: harvest.species_label ?? '', trees: harvest.trees_harvested, basis,
    formula: harvest.yield_formula === 'true_mid_girth' ? 'true_mid_girth' : 'hoppus', lines,
  });
  const totals = yieldTotals({ basis, lines });
  return (
    <div className="activity-labour">
      <dl className="detail-grid">
        <div><dt>Lines</dt><dd>{lines.length}</dd></div>
        {basis === 'log_volume' ? <>
          <div><dt>Logs</dt><dd>{formatNumber(totals.logs, 0)}</dd></div>
          <div><dt>Volume</dt><dd>{formatNumber(totals.volume, 3)} m³</dd></div>
        </> : <div><dt>Net weight</dt><dd>{formatNumber(totals.weight, 3)} t</dd></div>}
        <div><dt>Trees felled</dt><dd>{formatNumber(harvest.trees_harvested, 0)}</dd></div>
      </dl>
      <div className="inline-form-actions">
        <button onClick={() => window.open(URL.createObjectURL(new Blob([html()], { type: 'text/html' })), '_blank', 'noopener')} type="button">
          <ExternalLink size={14} /> Open / print
        </button>
        <button className="primary" disabled={busy} type="button" onClick={() => {
          setBusy(true);
          void import('@/lib/pdf').then(({ htmlToPdf }) => htmlToPdf(html(), `yield-statement-${harvest.harvest_code ?? 'harvest'}.pdf`))
            .then((file) => {
              const link = document.createElement('a');
              link.href = URL.createObjectURL(file);
              link.download = file.name;
              link.click();
            })
            .finally(() => setBusy(false));
        }}>
          <Download size={14} /> {busy ? 'Making the PDF...' : 'Download PDF'}
        </button>
      </div>
    </div>
  );
}
